type Listener = (...args: unknown[]) => void

class EventEmitter {
  private listeners = new Map<string, Set<Listener>>()

  addListener(event: string, listener: Listener) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set())
    this.listeners.get(event)!.add(listener)
    return { remove: () => this.removeListener(event, listener) }
  }

  removeListener(event: string, listener: Listener) {
    this.listeners.get(event)?.delete(listener)
  }

  emit(event: string, ...args: unknown[]) {
    this.listeners.get(event)?.forEach((listener) => listener(...args))
  }

  removeAllListeners(event?: string) {
    if (event) {
      this.listeners.delete(event)
      return
    }
    this.listeners.clear()
  }
}

export const DeviceEventEmitter = new EventEmitter()

type BackPressHandler = () => boolean

const hardwareBackPressHandlers = new Set<BackPressHandler>()

export const BackHandler = {
  addEventListener(event: string, handler: BackPressHandler) {
    if (event === "hardwareBackPress") {
      hardwareBackPressHandlers.add(handler)
    }
    return {
      remove: () => {
        hardwareBackPressHandlers.delete(handler)
      },
    }
  },
  removeEventListener(event: string, handler: BackPressHandler) {
    if (event === "hardwareBackPress") {
      hardwareBackPressHandlers.delete(handler)
    }
  },
  exitApp() {},
}

export const Keyboard = {
  dismiss() {
    if (typeof document !== "undefined") {
      ;(document.activeElement as HTMLElement | null)?.blur?.()
    }
  },
  addListener(event: string, handler: (e: { endCoordinates?: { height: number } }) => void) {
    if (typeof window === "undefined" || event !== "keyboardDidShow") {
      return { remove: () => {} }
    }
    const onResize = () => handler({ endCoordinates: { height: 0 } })
    window.visualViewport?.addEventListener("resize", onResize)
    return { remove: () => window.visualViewport?.removeEventListener("resize", onResize) }
  },
}

export const Linking = {
  async openURL(url: string) {
    window.open(url, "_blank", "noopener,noreferrer")
  },
  async canOpenURL() {
    return true
  },
  getInitialURL: async () => null,
  addEventListener: () => ({ remove: () => {} }),
}

export const Alert = {
  alert(title: string, message?: string) {
    window.alert(message ? `${title}\n${message}` : title)
  },
}

export const AppState = {
  currentState: "active",
  addEventListener: () => ({ remove: () => {} }),
}

export type AppStateStatus = "active" | "background" | "inactive"
